import { useState, useEffect, useCallback } from "react";
import { motion } from "framer-motion";
import { useNotifications } from "../../contexts/NotificationContext";

type Block = {
	x: number;
	y: number;
	color: string;
};

type Piece = {
	blocks: Block[];
	type: string;
};

const BOARD_WIDTH = 10;
const BOARD_HEIGHT = 20;
const COLORS = ["#FF0000", "#00FF00", "#0000FF", "#FFFF00", "#FF00FF", "#00FFFF"];

const PIECES = [
	// I
	{
		blocks: [
			{ x: 0, y: 0 },
			{ x: 1, y: 0 },
			{ x: 2, y: 0 },
			{ x: 3, y: 0 },
		],
		type: "I",
	},
	// O
	{
		blocks: [
			{ x: 0, y: 0 },
			{ x: 1, y: 0 },
			{ x: 0, y: 1 },
			{ x: 1, y: 1 },
		],
		type: "O",
	},
	// T
	{
		blocks: [
			{ x: 1, y: 0 },
			{ x: 0, y: 1 },
			{ x: 1, y: 1 },
			{ x: 2, y: 1 },
		],
		type: "T",
	},
	// L
	{
		blocks: [
			{ x: 2, y: 0 },
			{ x: 0, y: 1 },
			{ x: 1, y: 1 },
			{ x: 2, y: 1 },
		],
		type: "L",
	},
];

const Tetris = () => {
	const { addNotification } = useNotifications();
	const [isPlaying, setIsPlaying] = useState(false);
	const [score, setScore] = useState(0);
	const [board, setBoard] = useState<(string | null)[][]>(
		Array(BOARD_HEIGHT)
			.fill(null)
			.map(() => Array(BOARD_WIDTH).fill(null))
	);
	const [currentPiece, setCurrentPiece] = useState<Piece | null>(null);
	const [gameSpeed, setGameSpeed] = useState(1000);

	const generatePiece = useCallback(() => {
		const piece = PIECES[Math.floor(Math.random() * PIECES.length)];
		const color = COLORS[Math.floor(Math.random() * COLORS.length)];
		return {
			...piece,
			blocks: piece.blocks.map((block) => ({
				...block,
				x: block.x + Math.floor(BOARD_WIDTH / 2) - 1,
				y: block.y,
				color,
			})),
		};
	}, []);

	const checkCollision = useCallback(
		(piece: Piece, offsetX = 0, offsetY = 0) => {
			return piece.blocks.some((block) => {
				const newX = block.x + offsetX;
				const newY = block.y + offsetY;
				return (
					newX < 0 ||
					newX >= BOARD_WIDTH ||
					newY >= BOARD_HEIGHT ||
					(newY >= 0 && board[newY][newX] !== null)
				);
			});
		},
		[board]
	);

	const rotatePiece = useCallback(
		(piece: Piece) => {
			if (piece.type === "O") return piece;

			const center = piece.blocks[1];
			const rotated = {
				...piece,
				blocks: piece.blocks.map((block) => ({
					...block,
					x: center.x - (block.y - center.y),
					y: center.y + (block.x - center.x),
				})),
			};

			if (!checkCollision(rotated)) {
				return rotated;
			}

			return piece;
		},
		[checkCollision]
	);

	const handleKeyDown = useCallback(
		(e: KeyboardEvent) => {
			if (!isPlaying || !currentPiece) return;

			switch (e.key) {
				case "ArrowLeft":
					if (!checkCollision(currentPiece, -1, 0)) {
						setCurrentPiece({
							...currentPiece,
							blocks: currentPiece.blocks.map((block) => ({
								...block,
								x: block.x - 1,
							})),
						});
					}
					break;
				case "ArrowRight":
					if (!checkCollision(currentPiece, 1, 0)) {
						setCurrentPiece({
							...currentPiece,
							blocks: currentPiece.blocks.map((block) => ({
								...block,
								x: block.x + 1,
							})),
						});
					}
					break;
				case "ArrowDown":
					if (!checkCollision(currentPiece, 0, 1)) {
						setCurrentPiece({
							...currentPiece,
							blocks: currentPiece.blocks.map((block) => ({
								...block,
								y: block.y + 1,
							})),
						});
					}
					break;
				case "ArrowUp":
					setCurrentPiece(rotatePiece(currentPiece));
					break;
			}
		},
		[isPlaying, currentPiece, checkCollision, rotatePiece]
	);

	// Game loop
	useEffect(() => {
		if (!isPlaying || !currentPiece) return;

		const gameLoop = setInterval(() => {
			if (!checkCollision(currentPiece, 0, 1)) {
				setCurrentPiece({
					...currentPiece,
					blocks: currentPiece.blocks.map((block) => ({
						...block,
						y: block.y + 1,
					})),
				});
			} else {
				// Freeze piece
				const newBoard = [...board];
				currentPiece.blocks.forEach((block) => {
					if (block.y >= 0) {
						newBoard[block.y][block.x] = block.color;
					}
				});

				// Check for completed lines
				let linesCleared = 0;
				for (let y = BOARD_HEIGHT - 1; y >= 0; y--) {
					if (newBoard[y].every((cell) => cell !== null)) {
						newBoard.splice(y, 1);
						newBoard.unshift(Array(BOARD_WIDTH).fill(null));
						linesCleared++;
						y++;
					}
				}

				if (linesCleared > 0) {
					setScore((prev) => prev + linesCleared * 100);
					setGameSpeed((prev) => Math.max(prev * 0.95, 200));
				}

				setBoard(newBoard);

				// Game over check
				if (currentPiece.blocks.some((block) => block.y <= 0)) {
					setIsPlaying(false);
					addNotification({
						message: `Game Over! Score: ${score}`,
						type: "info",
						icon: "🎮",
					});
					return;
				}

				setCurrentPiece(generatePiece());
			}
		}, gameSpeed);

		return () => clearInterval(gameLoop);
	}, [
		isPlaying,
		currentPiece,
		board,
		checkCollision,
		generatePiece,
		gameSpeed,
		score,
		addNotification,
	]);

	useEffect(() => {
		window.addEventListener("keydown", handleKeyDown);
		return () => window.removeEventListener("keydown", handleKeyDown);
	}, [handleKeyDown]);

	const startGame = () => {
		setIsPlaying(true);
		setScore(0);
		setGameSpeed(1000);
		setBoard(
			Array(BOARD_HEIGHT)
				.fill(null)
				.map(() => Array(BOARD_WIDTH).fill(null))
		);
		setCurrentPiece(generatePiece());
	};

	return (
		<div className='relative h-96 bg-black/30 rounded-xl overflow-hidden'>
			{!isPlaying ? (
				<button
					onClick={startGame}
					className='absolute inset-0 flex flex-col items-center justify-center text-trump-yellow font-bold'>
					<span className='text-4xl mb-2'>🎮</span>
					<span className='text-xl'>START TETRIS</span>
					<span className='text-sm text-white/60 mt-2'>Use arrow keys to play!</span>
				</button>
			) : (
				<>
					<div className='absolute top-2 left-2 bg-black/50 px-4 py-2 rounded-full text-trump-yellow font-bold'>
						Score: {score}
					</div>
					<div
						className='absolute inset-4 grid gap-px bg-black/20'
						style={{
							gridTemplateColumns: `repeat(${BOARD_WIDTH}, 1fr)`,
							gridTemplateRows: `repeat(${BOARD_HEIGHT}, 1fr)`,
						}}>
						{/* Board */}
						{board.map((row, y) =>
							row.map((cell, x) => (
								<div
									key={`${x}-${y}`}
									className='bg-black/30'
									style={{
										backgroundColor: cell || undefined,
									}}
								/>
							))
						)}
						{/* Current Piece */}
						{currentPiece?.blocks.map((block, index) => (
							<motion.div
								key={index}
								className='absolute'
								style={{
									backgroundColor: block.color,
									width: `${100 / BOARD_WIDTH}%`,
									height: `${100 / BOARD_HEIGHT}%`,
									left: `${(block.x * 100) / BOARD_WIDTH}%`,
									top: `${(block.y * 100) / BOARD_HEIGHT}%`,
								}}
								initial={{ scale: 0 }}
								animate={{ scale: 1 }}
							/>
						))}
					</div>
				</>
			)}
		</div>
	);
};

export default Tetris;
